import { useState } from "react"

import type { TimePickerData, UIEvent } from "../types"
import { CardActions, CardButton, CardShell } from "./card_shell"
import { SelectInput, type SelectInputOption } from "./select_input"
import styles from "./time_picker_card.module.scss"

interface Props {
  ui: UIEvent
  onRespond: (uiId: string, actionId: string, value: unknown, displayText?: string) => void
}

function hourLabel(hour: number): string {
  const period = hour < 12 ? "AM" : "PM"
  const display = hour % 12 === 0 ? 12 : hour % 12
  return `${display} ${period}`
}

const HOUR_OPTIONS: SelectInputOption[] = Array.from({ length: 24 }, (_, hour) => ({
  value: String(hour).padStart(2, "0"),
  label: hourLabel(hour),
}))

const TimePickerCard = ({ ui, onRespond }: Props) => {
  const { prompt, mode, minute_options: minuteOptions, default: defaultValue } = ui.data as TimePickerData
  const [defaultHour, defaultMinute] = typeof defaultValue === "string" ? defaultValue.split(":") : []

  const [answered, setAnswered] = useState(false)
  const [hour, setHour] = useState(mode === "minutes_past_hour" ? "00" : defaultHour || "00")
  const [minute, setMinute] = useState(
    defaultMinute && minuteOptions.includes(defaultMinute) ? defaultMinute : minuteOptions[0]
  )

  const minuteSelectOptions: SelectInputOption[] = minuteOptions.map(value => ({ value, label: `${value} Minutes` }))

  const submit = () => {
    if (answered) return
    setAnswered(true)
    const value = `${hour}:${minute}`
    let displayText
    if (mode === "minutes_past_hour") {
      displayText = minute === "00" ? "On the hour" : `${Number(minute)} minutes past the hour`
    } else {
      const [hourPart, period] = hourLabel(Number(hour)).split(" ")
      displayText = `${hourPart}:${minute} ${period}`
    }
    onRespond(ui.id, "select", value, displayText)
  }

  return (
    <CardShell prompt={prompt} onDismiss={() => onRespond(ui.id, "close", undefined)}>
      <div className={styles["controls"]}>
        {mode === "time_of_day" ? (
          <SelectInput ariaLabel="Hour" value={hour} options={HOUR_OPTIONS} onChange={setHour} disabled={answered} />
        ) : null}
        <SelectInput
          ariaLabel="Minute"
          value={minute}
          options={minuteSelectOptions}
          onChange={setMinute}
          disabled={answered}
        />
      </div>
      <CardActions>
        <CardButton variant="primary" disabled={answered} onClick={submit}>
          Select
        </CardButton>
      </CardActions>
    </CardShell>
  )
}

export default TimePickerCard
